import type { PersonID, PersonData, PersonCollection as IPersonCollection } from '../../types';

/**
 * PersonCollection manages the collection of tracked people.
 * Provides methods for adding, removing, updating, and iterating over people.
 * 
 * Validates Requirements 10.1, 10.2
 */
export class PersonCollection {
  private people: Map<PersonID, PersonData>;
  private frameCount: number;

  constructor() {
    this.people = new Map();
    this.frameCount = 0;
  }

  /**
   * Add a new person to the collection.
   * @param personData Person data to add
   */
  add(personData: PersonData): void {
    this.people.set(personData.personId, personData);
  }

  /**
   * Remove a person from the collection by ID.
   * @param personId Person identifier to remove
   * @returns true if person was removed, false if not found
   */
  remove(personId: PersonID): boolean {
    return this.people.delete(personId);
  }

  /**
   * Update an existing person's data.
   * @param personData Updated person data
   * @returns true if person was updated, false if not found
   */
  update(personData: PersonData): boolean {
    if (!this.people.has(personData.personId)) {
      return false;
    }
    this.people.set(personData.personId, personData);
    return true;
  }

  /**
   * Get a person by ID.
   * @param personId Person identifier to look up
   * @returns Person data if found, undefined otherwise
   */
  get(personId: PersonID): PersonData | undefined {
    return this.people.get(personId);
  }

  /**
   * Check if a person exists in the collection.
   * @param personId Person identifier to check
   * @returns true if person exists, false otherwise
   */
  has(personId: PersonID): boolean {
    return this.people.has(personId);
  }

  /**
   * Get the number of people in the collection.
   * @returns Number of tracked people
   */
  size(): number {
    return this.people.size;
  }

  /**
   * Get all person IDs in the collection.
   * @returns Array of person identifiers
   */
  getPersonIds(): PersonID[] {
    return Array.from(this.people.keys());
  }

  /**
   * Get all person data in the collection.
   * @returns Array of person data
   */
  getAllPeople(): PersonData[] {
    return Array.from(this.people.values());
  }

  /**
   * Iterate over all people in the collection.
   * @param callback Function to call for each person
   */
  forEach(callback: (personData: PersonData) => void): void {
    this.people.forEach(callback);
  }

  /**
   * Clear all people from the collection.
   */
  clear(): void {
    this.people.clear();
  }

  /**
   * Get the current frame count.
   * @returns Current frame number
   */
  getFrameCount(): number {
    return this.frameCount;
  }

  /**
   * Increment the frame count.
   */
  incrementFrameCount(): void {
    this.frameCount++;
  }

  /**
   * Set the frame count to a specific value.
   * @param frameCount Frame number to set
   */
  setFrameCount(frameCount: number): void {
    this.frameCount = frameCount;
  }

  /**
   * Convert to the PersonCollection interface format.
   * @returns PersonCollection interface object
   */
  toInterface(): IPersonCollection {
    return {
      people: new Map(this.people),
      frameCount: this.frameCount
    };
  }

  /**
   * Create a PersonCollection from the interface format.
   * @param data PersonCollection interface object
   * @returns PersonCollection instance
   */
  static fromInterface(data: IPersonCollection): PersonCollection {
    const collection = new PersonCollection();
    collection.people = new Map(data.people);
    collection.frameCount = data.frameCount;
    return collection;
  }
}
